<template>
  <div class="card">
    <CardHeader :icon="icon" :title="title" :is-small="isTitleSmall" :has-separator="false">
      <template #action-button>
        <ButtonItem
          theme="neutral"
          icon-position="icon-only"
          left-icon="Edit"
          :is-disabled="editDisabled"
          size="small"
          @click="handleEditMode"
        />
      </template>
    </CardHeader>
    <div class="content">
      <slot name="content" />
    </div>
    <div v-if="editDisabled" class="card-footer">
      <ButtonItem :text="t('common.cancel')" theme="neutral" @click="handleEditMode" />
      <ButtonItem
        :text="t('common.save')"
        theme="primary"
        :is-disabled="isSubmitDisabled"
        :is-loading="loading"
        @click="editWallet"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ButtonItem, CardHeader } from '@lw-design-system/ui';

const { t, locale, getLocaleCookie, setLocale, setLocaleCookie } = useI18n();

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  editDisabled: {
    type: Boolean,
    default: false
  },
  isSubmitDisabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  isTitleSmall: {
    type: Boolean,
    default: false
  },
  hasSeparator: {
    type: Boolean,
    default: true
  }
});

const editMode = ref(props.editDisabled);

// const disableEdit = computed(() => props.editDisabled || );

const emit = defineEmits<{
  (e: 'update-edit', editValue: boolean): void;
  (e: 'handle-update'): void;
}>();

onMounted(() => {
  const cookie = getLocaleCookie();
  if (cookie && isSupportedLocale(cookie)) {
    setLocale(cookie);
  }
});

const handleEditMode = () => {
  editMode.value = !editMode.value;
  emit('update-edit', editMode.value);
};

const editWallet = async () => {
  await emit('handle-update');
  emit('update-edit', editMode.value);
};

watch(locale, async (selectedLocale: string) => {
  if (!isSupportedLocale(selectedLocale)) return;
  await setLocale(selectedLocale);
  setLocaleCookie(selectedLocale);
});
const dynamicBorderBottom = computed(() =>
  props.hasSeparator ? '0.0625rem solid var(--ui-separator)' : '0rem'
);
</script>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  background-color: var(--card-background);
  box-shadow: 0, var(--box-shadow-card);
  flex: 1;
  height: 100%;
}

.content {
  padding: 0rem 1.5rem 1.5rem 1.5rem;
  overflow-y: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  border-bottom: v-bind(dynamicBorderBottom);
  padding: 1.5rem;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 21.875rem;
}
</style>
